import { IResourceBase } from 'app/shared/utils/resourcefiles.utils';
import { ISaveResult } from 'app/interfaces/ISaveStatus';
import { BehaviorSubject } from 'rxjs';
import * as Constants from 'app/services/globals';
import { IAzureResource } from './IAzureResource';
import { eModelState } from 'app/model/services/state';
import { LoadingStatus } from 'shell-models-lib';
export interface ITagResult extends IResourceBase {
    payload: string;
}
export interface IFullResource extends IResourceBase {
    payload: string;
    status: LoadingStatus;
    etag?: string;
}

export type Operation = 'add' | 'delete' | 'rename' | 'update';

export interface ICommitChange {
    operation: Operation;
    resource: IResourceBase;
    payload?: ICommitPayload;
    notGoIntoGitRepo?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ICommitPayload {}

export interface IRenamePayload extends ICommitPayload {
    oldName: string;
}

export interface IDiff {
    changedFiles: IChangedFile[];
    aheadCount: number;
    behindCount: number;
    commonCommit: string;
}

export type ChangeType = 'edit' | 'add' | 'rename' | 'delete' | 'edit, rename' | 'unchanged';

export interface IChangedFile {
    filePath: string;
    changeType: ChangeType;
    originalFileName?: string;
    resourceType?: string;
}

export enum ResourceProviderLocation {
    Git,
    MetadataService,
    Local
}

export interface IResourceFilesProvider {
    readonly writeIdentities: boolean;
    readonly resourceProviderLocation: ResourceProviderLocation;
    resourceSubject: BehaviorSubject<IFullResource[]>;

    init(fullLoad: boolean, skipPopulateResource?: boolean): Promise<boolean>;
    reinitialize(fullLoad: boolean): Promise<boolean>;
    reinitializeResource(
        fullLoad: boolean,
        resourceType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentName?: string
    ): Promise<boolean>;
    reset(): void;
    resetResource(
        resourceType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentName?: string
    ): void;
    listResources(...resourceTypes: string[]): Promise<IFullResource[]>;
    listParentAndChildResources(
        resourceType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentName?: string
    ): Promise<IFullResource[]>;
    getResourceJSON(resource: IResourceBase): Promise<string>;
    getResourceObject(
        resourceType: Constants.ResourceType,
        uniqueKey: string,
        updateIfExisting?: boolean,
        options?: {
            workspaceObjectId?: string;
            resourceSubType?: string;
        }
    ): Promise<IFullResource>;
    getStoredResource(resource: IResourceBase): IFullResource | undefined;
    updateStoredResource(options: {
        operation: Extract<Operation, 'rename'>;
        resourceType: Constants.ResourceType;
        originalUniqueKey: string;
        newName: string;
    }): void;
    updateStoredResource(options: {
        operation: Extract<Operation, 'delete'>;
        resourceType: Constants.ResourceType;
        originalUniqueKey: string;
    }): void;
    updateStoredResource(options: {
        operation: Extract<Operation, 'update'>;
        resourceType: Constants.ResourceType;
        originalUniqueKey: string;
        payload: string;
    }): void;
    updateEtag(resourceType: Constants.ResourceType, uniqueKey: string, etag: string): void;
    updateExtraProperties?: (resourceType: Constants.ResourceType, uniqueKey: string, properties: {}) => void;
    commit(commitChanges: ICommitChange[], message?: string): Promise<ISaveResult>;

    // autosave
    fetchResourcesInUserStore(resourceType: Constants.ResourceType): Promise<IAzureResource[]>;
    getResourceStateBySource(identity: string, resourceType?: Constants.ResourceType): Promise<eModelState[]>;
    getRenamedName(resourcetype: Constants.ResourceType, originalName: string): string | undefined;
    mergeResources(
        baseResources: IAzureResource[],
        resources: IAzureResource[],
        resourceType: Constants.ResourceType
    ): IAzureResource[];
    mergeResources(
        baseResources: IFullResource[],
        resources: IFullResource[],
        resourceType: Constants.ResourceType
    ): IFullResource[];
}

export interface IDiffProvider {
    getIncomingChanges(): Promise<IDiff>;
    getUserChanges(): Promise<IDiff>;
}

export interface ICreateUpdateFilePayload {
    payload: string;
    fileName: string;
    add: boolean;
}
